import XLSX from "xlsx";
import ExcelJS from "exceljs";
import { Chart } from "chart.js";

const DEFAULT_STYLE = {
  bgcolor: "#ffffff",
  align: "left",
  valign: "middle",
  textwrap: false,
  strike: false,
  underline: false,
  color: "#0a0a0a",
  font: {
    name: "Arial",
    size: 10,
    bold: false,
    italic: false,
  },
  format: "normal",
};

function deepMergeStyle(base, override) {
  if (!override) return base;
  const out = { ...base };
  for (const [k, v] of Object.entries(override)) {
    if (v == null) continue;
    if (k === "font" && typeof v === "object") {
      out.font = { ...(out.font || {}), ...v };
    } else if (k === "border" && typeof v === "object") {
      out.border = { ...(out.border || {}), ...v };
    } else {
      out[k] = v;
    }
  }
  return out;
}

function normalizeHexColorToArgb(color) {
  if (!color) return undefined;
  const raw = String(color).trim();
  const hex = raw.startsWith("#") ? raw.slice(1) : raw;
  if (/^[0-9a-fA-F]{6}$/.test(hex)) return `FF${hex.toUpperCase()}`;
  if (/^[0-9a-fA-F]{8}$/.test(hex)) return hex.toUpperCase();
  return undefined;
}

function pxToRowHeightPoints(px) {
  if (typeof px !== "number" || !Number.isFinite(px) || px <= 0) return undefined;
  return Math.round(px * 0.75 * 100) / 100;
}

function pxToColWidthChars(px) {
  if (typeof px !== "number" || !Number.isFinite(px) || px <= 0) return undefined;
  return Math.round((px / 7) * 100) / 100;
}

function mapAlign(align) {
  const a = String(align || "").toLowerCase();
  if (a === "center") return "center";
  if (a === "right") return "right";
  return "left";
}

function mapValign(valign) {
  const v = String(valign || "").toLowerCase();
  if (v === "top") return "top";
  if (v === "bottom") return "bottom";
  return "middle";
}

function mapBorderStyle(style) {
  const s = String(style || "").toLowerCase();
  if (s === "thin" || s === "medium" || s === "thick" || s === "dotted" || s === "dashed" || s === "double") {
    return s;
  }
  // x-data-spreadsheet sometimes uses numeric or custom tokens; default to thin
  return "thin";
}

function formatKeyToExcelNumFmt(key) {
  switch (key) {
    case "text":
      return "@";
    case "number":
      return "#,##0.00";
    case "percent":
      return "0.00%";
    case "rmb":
      return "¥#,##0.00";
    case "usd":
      return "$#,##0.00";
    case "eur":
      return "€#,##0.00";
    case "date":
      return "dd/mm/yyyy";
    case "time":
      return "hh:mm:ss";
    case "datetime":
      return "dd/mm/yyyy hh:mm:ss";
    case "duration":
      return "[h]:mm:ss";
    case "normal":
    default:
      return undefined;
  }
}

function parseCellTextToExcelJsValue(text, formatKey) {
  if (text == null) return null;
  const raw = String(text);
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  // Explicit text marker (like Excel): leading apostrophe forces text.
  // In Excel the apostrophe is not displayed in-cell, but appears in the formula bar.
  if (trimmed[0] === "'") {
    return trimmed.slice(1);
  }

  if (trimmed[0] === "=") {
    return { formula: trimmed.slice(1) };
  }

  const upper = trimmed.toUpperCase();
  if (upper === "TRUE" || upper === "FALSE") return upper === "TRUE";

  const fmt = String(formatKey || "").toLowerCase();

  // Percent convenience: allow "10%" -> 0.1 (Excel stores percents as fractions)
  if (fmt === "percent" && /^-?\d+(\.\d+)?%$/.test(trimmed)) {
    const n = Number(trimmed.slice(0, -1));
    if (Number.isFinite(n)) return n / 100;
  }

  // General numeric parsing:
  // - Treat numeric-looking strings as numbers so Excel doesn't flag them as text.
  // - Preserve leading-zero codes (e.g. 00123) as text.
  // - Support thousands separators (e.g. 1,234.56).
  const hasLeadingZero = /^-?0\d+/.test(trimmed);
  const isPlainNumber = /^-?\d+(\.\d+)?$/.test(trimmed);
  const isCommaNumber = /^-?\d{1,3}(,\d{3})+(\.\d+)?$/.test(trimmed);

  if (!hasLeadingZero && (isPlainNumber || isCommaNumber) && fmt !== "text") {
    const normalized = isCommaNumber ? trimmed.replace(/,/g, "") : trimmed;
    const n = Number(normalized);
    if (Number.isFinite(n)) return n;
  }

  // Avoid aggressive date parsing (locale ambiguity). Keep as string.
  return raw;
}

function computeUsedRangeExtended(sheet) {
  const base = computeUsedRange(sheet);
  let { maxRow, maxCol } = base;

  // Include explicit row/col definitions (width/height/hide/style)
  const rows = sheet?.rows || {};
  for (const riKey of Object.keys(rows)) {
    if (riKey === "len") continue;
    const ri = Number(riKey);
    if (Number.isInteger(ri) && ri >= 0) maxRow = Math.max(maxRow, ri);
  }
  const cols = sheet?.cols || {};
  for (const ciKey of Object.keys(cols)) {
    if (ciKey === "len") continue;
    const ci = Number(ciKey);
    if (Number.isInteger(ci) && ci >= 0) maxCol = Math.max(maxCol, ci);
  }

  // Include autofilter range
  const ref = sheet?.autofilter?.ref;
  if (ref) {
    try {
      const decoded = XLSX.utils.decode_range(ref);
      maxRow = Math.max(maxRow, decoded.e.r);
      maxCol = Math.max(maxCol, decoded.e.c);
    } catch {
      // ignore
    }
  }

  // Include freeze split cell
  const freeze = sheet?.freeze;
  if (freeze && typeof freeze === "string") {
    try {
      const decoded = XLSX.utils.decode_cell(freeze);
      maxRow = Math.max(maxRow, decoded.r);
      maxCol = Math.max(maxCol, decoded.c);
    } catch {
      // ignore
    }
  }

  return { maxRow, maxCol };
}

function sanitizeSheetName(name, fallback) {
  const base = (name || fallback || "Sheet").toString();
  // Excel constraints: max 31 chars; cannot contain: : \ / ? * [ ]
  const cleaned = base.replace(/[:\\/?*\[\]]/g, " ").trim();
  const limited = cleaned.length > 0 ? cleaned.slice(0, 31) : (fallback || "Sheet");
  return limited;
}

function needsExcelSheetQuoting(sheetName) {
  // Excel allows unquoted sheet refs only when the name is a simple identifier.
  // Be conservative: quote anything with spaces or non-word chars.
  return /[^A-Za-z0-9_]/.test(sheetName);
}

function quoteExcelSheetName(sheetName) {
  // In Excel formulas, single quotes inside sheet names are escaped by doubling.
  const escaped = String(sheetName).replace(/'/g, "''");
  return `'${escaped}'`;
}

function sheetRefToken(sheetName) {
  const name = String(sheetName);
  return needsExcelSheetQuoting(name) ? quoteExcelSheetName(name) : name;
}

function escapeForRegex(literal) {
  return String(literal).replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function rewriteFormulaSheetRefs(formula, sheetNameMap) {
  if (!formula || typeof formula !== "string") return formula;
  if (!sheetNameMap || sheetNameMap.size === 0) return formula;

  let out = formula;

  // Replace both quoted and unquoted sheet tokens.
  // Examples:
  //   Sheet1!A1
  //   'Sheet 1'!A1
  //   SUM(Sheet1!A1, 'Sheet 2'!B3)
  for (const [oldNameRaw, newNameRaw] of sheetNameMap.entries()) {
    const oldName = String(oldNameRaw ?? "");
    const newName = String(newNameRaw ?? "");
    if (!oldName || !newName || oldName === newName) continue;

    const newToken = sheetRefToken(newName);

    // 1) Quoted old name: 'Old Name'!
    // We escape single quotes within the name to match Excel-escaped form.
    const oldQuotedToken = quoteExcelSheetName(oldName);
    const quotedPattern = new RegExp(`${escapeForRegex(oldQuotedToken)}!`, "g");
    out = out.replace(quotedPattern, `${newToken}!`);

    // 2) Unquoted old name: OldName!
    // Only safe to attempt when the old name itself doesn't contain whitespace,
    // because unquoted refs with spaces are invalid in Excel.
    if (!/\s/.test(oldName)) {
      const unquotedPattern = new RegExp(`(^|[^A-Za-z0-9_])${escapeForRegex(oldName)}!`, "g");
      out = out.replace(unquotedPattern, `$1${newToken}!`);
    } else {
      // Best-effort for names with spaces used unquoted by the app.
      out = out.split(`${oldName}!`).join(`${newToken}!`);
    }
  }

  return out;
}

function parseCellTextToXlsxValue(text) {
  if (text == null) return null;
  const raw = String(text);
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;

  if (trimmed[0] === "=") {
    // Formula without leading '='
    return { f: trimmed.slice(1) };
  }

  const upper = trimmed.toUpperCase();
  if (upper === "TRUE" || upper === "FALSE") {
    return { t: "b", v: upper === "TRUE" };
  }

  // Simple number parsing (avoid changing values with leading zeros like 00123)
  if (/^-?\d+(\.\d+)?$/.test(trimmed)) {
    const hasLeadingZero = /^-?0\d+/.test(trimmed);
    if (!hasLeadingZero) {
      const n = Number(trimmed);
      if (!Number.isNaN(n) && Number.isFinite(n)) {
        return { t: "n", v: n };
      }
    }
  }

  return { t: "s", v: raw };
}

function computeUsedRange(sheet) {
  const rows = sheet?.rows || {};
  let maxRow = -1;
  let maxCol = -1;

  for (const riKey of Object.keys(rows)) {
    if (riKey === "len") continue;
    const ri = Number(riKey);
    if (!Number.isInteger(ri) || ri < 0) continue;
    const row = rows[riKey];
    const cells = row?.cells;
    if (!cells) continue;

    for (const ciKey of Object.keys(cells)) {
      const ci = Number(ciKey);
      if (!Number.isInteger(ci) || ci < 0) continue;
      const cell = cells[ciKey];
      const text = cell?.text;
      if (text == null) continue;
      if (String(text).length === 0) continue;
      if (ri > maxRow) maxRow = ri;
      if (ci > maxCol) maxCol = ci;
    }
  }

  // If there are merges, ensure range includes them
  const merges = Array.isArray(sheet?.merges) ? sheet.merges : [];
  for (const merge of merges) {
    try {
      const decoded = XLSX.utils.decode_range(merge);
      maxRow = Math.max(maxRow, decoded.e.r);
      maxCol = Math.max(maxCol, decoded.e.c);
    } catch {
      // ignore invalid merge refs
    }
  }

  return { maxRow, maxCol };
}

function getResolvedCellStyle(sheet, ri, ci, cell) {
  const styles = Array.isArray(sheet?.styles) ? sheet.styles : [];
  const row = sheet?.rows?.[ri];
  const col = sheet?.cols?.[ci];

  let merged = { ...DEFAULT_STYLE };
  if (row && row.style !== undefined) merged = deepMergeStyle(merged, styles[row.style]);
  if (col && col.style !== undefined) merged = deepMergeStyle(merged, styles[col.style]);
  if (cell && cell.style !== undefined) merged = deepMergeStyle(merged, styles[cell.style]);
  return merged;
}

function applyExcelJsStyle(excelCell, resolvedStyle) {
  if (!resolvedStyle) return;

  const fontColor = normalizeHexColorToArgb(resolvedStyle.color);
  const fontName = resolvedStyle.font?.name;
  const fontSize = resolvedStyle.font?.size;
  excelCell.font = {
    name: fontName || "Arial",
    size: typeof fontSize === "number" && Number.isFinite(fontSize) ? fontSize : 10,
    bold: !!resolvedStyle.font?.bold,
    italic: !!resolvedStyle.font?.italic,
    underline: !!resolvedStyle.underline,
    strike: !!resolvedStyle.strike,
    ...(fontColor ? { color: { argb: fontColor } } : {}),
  };

  excelCell.alignment = {
    horizontal: mapAlign(resolvedStyle.align),
    vertical: mapValign(resolvedStyle.valign),
    wrapText: !!resolvedStyle.textwrap,
  };

  // IMPORTANT: Excel gridlines are hidden when a cell has a fill.
  // x-data-spreadsheet's "default" background is white; exporting that as an
  // explicit fill makes the whole sheet look like it has no borders.
  const fillArgb = normalizeHexColorToArgb(resolvedStyle.bgcolor);
  const isExplicitWhite = fillArgb === "FFFFFFFF";
  if (fillArgb && !isExplicitWhite) {
    excelCell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: fillArgb },
    };
  }

  const numFmt = formatKeyToExcelNumFmt(resolvedStyle.format);
  if (numFmt) {
    excelCell.numFmt = numFmt;
  }

  const border = resolvedStyle.border;
  if (border && typeof border === "object") {
    const makeSide = (side) => {
      const v = border?.[side];
      if (!v) return undefined;
      const [style, color] = Array.isArray(v) ? v : [v.style, v.color];
      const argb = normalizeHexColorToArgb(color);
      return {
        style: mapBorderStyle(style),
        ...(argb ? { color: { argb } } : {}),
      };
    };
    const excelBorder = {
      top: makeSide("top"),
      left: makeSide("left"),
      bottom: makeSide("bottom"),
      right: makeSide("right"),
    };
    if (excelBorder.top || excelBorder.left || excelBorder.bottom || excelBorder.right) {
      excelCell.border = excelBorder;
    }
  }
}

function applyFreezePane(worksheet, freezeRef) {
  if (!freezeRef || typeof freezeRef !== "string") return;
  try {
    const decoded = XLSX.utils.decode_cell(freezeRef);
    const frozenRows = decoded.r;
    const frozenCols = decoded.c;
    if (frozenRows > 0 || frozenCols > 0) {
      worksheet.views = [
        {
          state: "frozen",
          xSplit: frozenCols,
          ySplit: frozenRows,
        },
      ];
    }
  } catch {
    // ignore
  }
}

function applyAutoFilter(worksheet, autofilter) {
  const ref = autofilter?.ref;
  if (!ref || typeof ref !== "string") return;
  // ExcelJS accepts a string range like "A1:D10"
  worksheet.autoFilter = ref;
}

function mapValidationToExcelJs(v) {
  const type = v?.type;
  const required = !!v?.required;
  const operator = v?.operator;
  const value = v?.value;

  // ExcelJS validation object
  const base = {
    allowBlank: !required,
    showErrorMessage: true,
  };

  if (type === "list") {
    const list = String(value || "").split(",").map((s) => s.trim());
    const joined = list.join(",");
    return {
      ...base,
      type: "list",
      formulae: [`"${joined.replace(/"/g, '""')}"`],
    };
  }

  if (type === "number") {
    const operatorMap = {
      be: "between",
      nbe: "notBetween",
      eq: "equal",
      neq: "notEqual",
      lt: "lessThan",
      lte: "lessThanOrEqual",
      gt: "greaterThan",
      gte: "greaterThanOrEqual",
    };
    const op = operatorMap[operator];
    if (!op) {
      return { ...base, type: "decimal" };
    }
    if (operator === "be" || operator === "nbe") {
      const [min, max] = Array.isArray(value) ? value : [undefined, undefined];
      return { ...base, type: "decimal", operator: op, formulae: [min, max] };
    }
    return { ...base, type: "decimal", operator: op, formulae: [value] };
  }

  if (type === "date") {
    const operatorMap = {
      be: "between",
      nbe: "notBetween",
      eq: "equal",
      neq: "notEqual",
      lt: "lessThan",
      lte: "lessThanOrEqual",
      gt: "greaterThan",
      gte: "greaterThanOrEqual",
    };
    const op = operatorMap[operator];
    if (!op) return { ...base, type: "date" };
    if (operator === "be" || operator === "nbe") {
      const [min, max] = Array.isArray(value) ? value : [undefined, undefined];
      return { ...base, type: "date", operator: op, formulae: [min, max] };
    }
    return { ...base, type: "date", operator: op, formulae: [value] };
  }

  // phone/email validations don't have a clean Excel equivalent; skip
  return null;
}

function applyValidations(worksheet, validations) {
  const items = Array.isArray(validations) ? validations : [];
  for (const item of items) {
    const refs = Array.isArray(item?.refs) ? item.refs : [];
    const excelValidation = mapValidationToExcelJs(item);
    if (!excelValidation) continue;
    for (const ref of refs) {
      if (!ref) continue;
      try {
        worksheet.dataValidations.add(ref, excelValidation);
      } catch {
        // ignore invalid ranges/validations
      }
    }
  }
}

function applyMergesAndPropagateStyles(worksheet, merges) {
  const mergeRefs = Array.isArray(merges) ? merges : [];
  for (const mergeRef of mergeRefs) {
    try {
      const decoded = XLSX.utils.decode_range(mergeRef);
      const startRow = decoded.s.r + 1;
      const startCol = decoded.s.c + 1;
      const endRow = decoded.e.r + 1;
      const endCol = decoded.e.c + 1;
      worksheet.mergeCells(startRow, startCol, endRow, endCol);

      const topLeft = worksheet.getCell(startRow, startCol);
      for (let r = startRow; r <= endRow; r += 1) {
        for (let c = startCol; c <= endCol; c += 1) {
          if (r === startRow && c === startCol) continue;
          const cell = worksheet.getCell(r, c);
          // Copy styles so the merged region looks consistent in Excel
          cell.style = { ...topLeft.style };
        }
      }
    } catch {
      // ignore
    }
  }
}

export async function xDataSpreadsheetToXlsxBlob(sheets, fileName = "Untitled") {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = "MileGrid";
  workbook.created = new Date();

  const sheetArray = Array.isArray(sheets) ? sheets : [];
  const seenNames = new Set();

  // Build a stable mapping of original -> final sheet names so we can rewrite
  // sheet-to-sheet formulas after sanitization / de-duplication.
  const sheetNameMap = new Map();
  const computedFinalNames = [];

  sheetArray.forEach((sheet, index) => {
    const baseName = sanitizeSheetName(sheet?.name, `Sheet ${index + 1}`);
    let finalName = baseName;
    let counter = 1;
    while (seenNames.has(finalName)) {
      const suffix = ` (${counter})`;
      finalName = sanitizeSheetName(baseName.slice(0, 31 - suffix.length) + suffix, baseName);
      counter += 1;
    }
    seenNames.add(finalName);

    computedFinalNames[index] = finalName;
    const originalName = (sheet?.name ?? "").toString();
    if (originalName && !sheetNameMap.has(originalName)) {
      sheetNameMap.set(originalName, finalName);
    }
  });

  // Reset for actual worksheet creation loop (names already computed)
  seenNames.clear();

  sheetArray.forEach((sheet, index) => {
    const finalName = computedFinalNames[index] || sanitizeSheetName(sheet?.name, `Sheet ${index + 1}`);
    const worksheet = workbook.addWorksheet(finalName);

    const { maxRow, maxCol } = computeUsedRangeExtended(sheet);
    const maxRowSafe = Math.max(0, maxRow);
    const maxColSafe = Math.max(0, maxCol);

    // Column properties
    const cols = sheet?.cols || {};
    for (let ci = 0; ci <= maxColSafe; ci += 1) {
      const col = cols[ci];
      const excelCol = worksheet.getColumn(ci + 1);
      const width = pxToColWidthChars(col?.width);
      if (width) excelCol.width = width;
      if (col?.hide) excelCol.hidden = true;
    }

    // Row properties
    const rows = sheet?.rows || {};
    for (let ri = 0; ri <= maxRowSafe; ri += 1) {
      const row = rows[ri];
      const excelRow = worksheet.getRow(ri + 1);
      const height = pxToRowHeightPoints(row?.height);
      if (height) excelRow.height = height;
      if (row?.hide) excelRow.hidden = true;
    }

    // Cells
    for (const riKey of Object.keys(rows)) {
      if (riKey === "len") continue;
      const ri = Number(riKey);
      if (!Number.isInteger(ri) || ri < 0 || ri > maxRowSafe) continue;
      const row = rows[riKey];
      const cells = row?.cells;
      if (!cells) continue;

      for (const ciKey of Object.keys(cells)) {
        const ci = Number(ciKey);
        if (!Number.isInteger(ci) || ci < 0 || ci > maxColSafe) continue;

        const cell = cells[ciKey];
        const resolvedStyle = getResolvedCellStyle(sheet, riKey, ciKey, cell);
        const excelCell = worksheet.getCell(ri + 1, ci + 1);

        const excelValue = parseCellTextToExcelJsValue(cell?.text, resolvedStyle.format);
        if (excelValue && typeof excelValue === "object" && "formula" in excelValue) {
          const rewritten = rewriteFormulaSheetRefs(excelValue.formula, sheetNameMap);
          excelCell.value = { ...excelValue, formula: rewritten };
        } else if (excelValue !== null) {
          excelCell.value = excelValue;
        }

        applyExcelJsStyle(excelCell, resolvedStyle);
      }
    }

    // Freeze panes, autofilter, merges, validations
    applyFreezePane(worksheet, sheet?.freeze);
    applyAutoFilter(worksheet, sheet?.autofilter);
    applyMergesAndPropagateStyles(worksheet, sheet?.merges);
    applyValidations(worksheet, sheet?.validations);

    // Charts: embed as images (ExcelJS doesn't generate native chart XML)
    try {
      embedChartsAsImages({
        workbook,
        worksheet,
        sheet,
        usedRange: { maxRow: maxRowSafe, maxCol: maxColSafe },
      });
    } catch (e) {
      // Best-effort; don't fail export if chart rendering fails
      // eslint-disable-next-line no-console
      console.warn("Chart export failed:", e);
    }
  });

  if (workbook.worksheets.length === 0) {
    workbook.addWorksheet("Sheet 1");
  }

  const buffer = await workbook.xlsx.writeBuffer();
  const safeName = (fileName || "Untitled").toString();
  return new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    name: `${safeName}.xlsx`,
  });
}

function parseChartRangeAnchor(dataRange) {
  if (!dataRange) return null;

  // Serialized CellRange object from x-data-spreadsheet
  if (typeof dataRange === "object") {
    const sri = Number(dataRange.sri);
    const sci = Number(dataRange.sci);
    const eri = Number(dataRange.eri);
    const eci = Number(dataRange.eci);
    if ([sri, sci].every(Number.isFinite)) {
      return {
        sri: Number.isFinite(sri) ? sri : 0,
        sci: Number.isFinite(sci) ? sci : 0,
        eri: Number.isFinite(eri) ? eri : sri,
        eci: Number.isFinite(eci) ? eci : sci,
      };
    }
  }

  // Range string like "A1:B10"
  if (typeof dataRange === "string") {
    try {
      const decoded = XLSX.utils.decode_range(dataRange);
      return {
        sri: decoded.s.r,
        sci: decoded.s.c,
        eri: decoded.e.r,
        eci: decoded.e.c,
      };
    } catch {
      return null;
    }
  }

  return null;
}

function chartToPngDataUrl(chartDef, { width, height }) {
  if (!chartDef || !chartDef.isValidChart) return null;
  if (!chartDef.data || !chartDef.chartType) return null;
  if (typeof document === "undefined") return null;

  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;

  // Ensure a deterministic white background and a visible frame.
  // (ExcelJS image embedding can't add borders, so we bake it into the bitmap.)
  const ctx = canvas.getContext("2d");
  if (ctx) {
    ctx.save();
    ctx.fillStyle = "#FFFFFF";
    ctx.fillRect(0, 0, width, height);
    ctx.restore();
  }

  const options = {
    ...(chartDef.options || {}),
    responsive: false,
    maintainAspectRatio: false,
    animation: false,
  };

  // Chart.js v2 accepts canvas element directly (matches existing app usage)
  const chart = new Chart(canvas, {
    type: chartDef.chartType,
    data: JSON.parse(JSON.stringify(chartDef.data)),
    options,
  });

  try {
    if (typeof chart.update === "function") chart.update(0);

    // Draw border after chart render
    if (ctx) {
      ctx.save();
      ctx.strokeStyle = "#C0C0C0";
      ctx.lineWidth = 2;
      ctx.strokeRect(1, 1, width - 2, height - 2);
      ctx.restore();
    }

    if (typeof chart.toBase64Image === "function") {
      return chart.toBase64Image();
    }
    return canvas.toDataURL("image/png");
  } finally {
    if (typeof chart.destroy === "function") chart.destroy();
  }
}

function embedChartsAsImages({ workbook, worksheet, sheet, usedRange }) {
  const charts = Array.isArray(sheet?.charts) ? sheet.charts : [];
  if (charts.length === 0) return;

  // Default size matches in-app chart container (without controls)
  const chartWidthPx = 560;
  const chartHeightPx = 300;

  // Placement strategy:
  // - Primary: place chart near its original cell range (dataRange) so it stays
  //   roughly where the user created it.
  // - Fallback: stack charts below the used grid.
  // Note: The in-app chart "position" is CSS fixed pixels (screen-space), which
  // isn't meaningfully mappable to Excel cells. `dataRange` is the reliable anchor.
  const fallbackStartRow0 = Math.min(Math.max((usedRange?.maxRow ?? 0) + 2, 0), 1048575);
  let fallbackNextRow0 = fallbackStartRow0;
  const fallbackCol0 = 0;
  const fallbackRowStep = 22;

  charts.forEach((chartDef, idx) => {
    const pngDataUrl = chartToPngDataUrl(chartDef, { width: chartWidthPx, height: chartHeightPx });
    if (!pngDataUrl) {
      // If chart is invalid, write the message into the sheet instead.
      const msg = chartDef?.message || "(Chart could not be rendered)";
      const row1 = Math.min(fallbackNextRow0 + 1, 1048576);
      const col1 = fallbackCol0 + 1;
      worksheet.getCell(row1, col1).value = msg;
      fallbackNextRow0 = Math.min(fallbackNextRow0 + fallbackRowStep, 1048575);
      return;
    }

    const imageId = workbook.addImage({ base64: pngDataUrl, extension: "png" });

    const anchor = parseChartRangeAnchor(chartDef?.dataRange);

    // Excel limits: cols [0..16383], rows [0..1048575]
    // Put chart to the right of the selected range (like the UI typically does),
    // and apply a small per-index offset to reduce overlap.
    let col0 = fallbackCol0;
    let row0 = fallbackNextRow0;

    if (anchor) {
      col0 = Math.min(Math.max(anchor.eci + 1, 0), 16383);
      row0 = Math.min(Math.max(anchor.sri, 0), 1048575);

      // If placing to the right would overflow, place below the range instead.
      if (col0 >= 16380) {
        col0 = Math.min(Math.max(anchor.sci, 0), 16383);
        row0 = Math.min(Math.max(anchor.eri + 1, 0), 1048575);
      }

      // Nudge subsequent charts down a bit if multiple share same anchor.
      row0 = Math.min(row0 + idx * 2, 1048575);
    }

    worksheet.addImage(imageId, {
      tl: { col: col0, row: row0 },
      ext: { width: chartWidthPx, height: chartHeightPx },
    });

    if (!anchor) {
      fallbackNextRow0 = Math.min(fallbackNextRow0 + fallbackRowStep, 1048575);
    }
  });
}

function buildWorksheetFromXDataSheet(sheet) {
  const ws = {};
  const { maxRow, maxCol } = computeUsedRange(sheet);

  if (maxRow < 0 || maxCol < 0) {
    ws["!ref"] = "A1";
  } else {
    const rows = sheet?.rows || {};
    for (const riKey of Object.keys(rows)) {
      if (riKey === "len") continue;
      const ri = Number(riKey);
      if (!Number.isInteger(ri) || ri < 0 || ri > maxRow) continue;

      const row = rows[riKey];
      const cells = row?.cells;
      if (!cells) continue;

      for (const ciKey of Object.keys(cells)) {
        const ci = Number(ciKey);
        if (!Number.isInteger(ci) || ci < 0 || ci > maxCol) continue;
        const cell = cells[ciKey];
        const value = parseCellTextToXlsxValue(cell?.text);
        if (!value) continue;

        const addr = XLSX.utils.encode_cell({ r: ri, c: ci });
        if (value.f && value.v === undefined && value.t === undefined) {
          ws[addr] = { f: value.f };
        } else {
          ws[addr] = value;
        }
      }
    }

    ws["!ref"] = XLSX.utils.encode_range({ s: { r: 0, c: 0 }, e: { r: maxRow, c: maxCol } });
  }

  // Merges
  const merges = Array.isArray(sheet?.merges) ? sheet.merges : [];
  const wsMerges = [];
  for (const mergeRef of merges) {
    try {
      wsMerges.push(XLSX.utils.decode_range(mergeRef));
    } catch {
      // ignore
    }
  }
  if (wsMerges.length > 0) {
    ws["!merges"] = wsMerges;
  }

  // Column widths (x-data-spreadsheet stores widths in px)
  const cols = sheet?.cols || {};
  const wsCols = [];
  const usedMaxCol = Math.max(0, maxCol);
  for (let ci = 0; ci <= usedMaxCol; ci += 1) {
    const col = cols[ci];
    const width = col?.width;
    if (typeof width === "number" && Number.isFinite(width) && width > 0) {
      wsCols[ci] = { wpx: Math.round(width) };
    }
  }
  if (wsCols.length > 0) {
    ws["!cols"] = wsCols;
  }

  // Row heights (px)
  const rows = sheet?.rows || {};
  const wsRows = [];
  const usedMaxRow = Math.max(0, maxRow);
  for (let ri = 0; ri <= usedMaxRow; ri += 1) {
    const row = rows[ri];
    const height = row?.height;
    if (typeof height === "number" && Number.isFinite(height) && height > 0) {
      wsRows[ri] = { hpx: Math.round(height) };
    }
  }
  if (wsRows.length > 0) {
    ws["!rows"] = wsRows;
  }

  return ws;
}

export function xDataSpreadsheetToWorkbook(sheets) {
  const wb = XLSX.utils.book_new();

  const sheetArray = Array.isArray(sheets) ? sheets : [];
  const seenNames = new Set();

  sheetArray.forEach((sheet, index) => {
    const baseName = sanitizeSheetName(sheet?.name, `Sheet ${index + 1}`);
    let finalName = baseName;
    let counter = 1;
    while (seenNames.has(finalName)) {
      const suffix = ` (${counter})`;
      finalName = sanitizeSheetName(baseName.slice(0, 31 - suffix.length) + suffix, baseName);
      counter += 1;
    }
    seenNames.add(finalName);

    const ws = buildWorksheetFromXDataSheet(sheet);
    XLSX.utils.book_append_sheet(wb, ws, finalName);
  });

  if (wb.SheetNames.length === 0) {
    const ws = { "!ref": "A1" };
    XLSX.utils.book_append_sheet(wb, ws, "Sheet 1");
  }

  return wb;
}

// Helper function to convert column letter to index (A=0, B=1, ..., Z=25, AA=26, etc.)
function columnToIndex(col) {
  let index = 0;
  for (let i = 0; i < col.length; i++) {
    index = index * 26 + (col.charCodeAt(i) - 64);
  }
  return index - 1;
}

// Helper function to convert column index to letter (0=A, 1=B, ..., 25=Z, 26=AA, etc.)
function indexToColumn(index) {
  let col = '';
  let num = index + 1;
  while (num > 0) {
    const remainder = (num - 1) % 26;
    col = String.fromCharCode(65 + remainder) + col;
    num = Math.floor((num - 1) / 26);
  }
  return col;
}

// Excel theme colors (Office theme defaults)
const EXCEL_THEME_COLORS = [
  'FFFFFF', // 0 - lt1 (background 1)
  '000000', // 1 - dk1 (text 1)
  'E7E6E6', // 2 - lt2 (background 2)  
  '44546A', // 3 - dk2 (text 2)
  '4472C4', // 4 - accent1
  'ED7D31', // 5 - accent2
  'A5A5A5', // 6 - accent3
  'FFC000', // 7 - accent4
  '5B9BD5', // 8 - accent5
  '70AD47', // 9 - accent6
];

// Apply tint to a color
function applyTint(hexColor, tint) {
  if (!tint || tint === 0) return hexColor;
  
  let r = parseInt(hexColor.slice(0, 2), 16);
  let g = parseInt(hexColor.slice(2, 4), 16);
  let b = parseInt(hexColor.slice(4, 6), 16);
  
  if (tint > 0) {
    // Lighten
    r = Math.round(r + (255 - r) * tint);
    g = Math.round(g + (255 - g) * tint);
    b = Math.round(b + (255 - b) * tint);
  } else {
    // Darken
    r = Math.round(r * (1 + tint));
    g = Math.round(g * (1 + tint));
    b = Math.round(b * (1 + tint));
  }
  
  r = Math.max(0, Math.min(255, r));
  g = Math.max(0, Math.min(255, g));
  b = Math.max(0, Math.min(255, b));
  
  return r.toString(16).padStart(2, '0') + 
         g.toString(16).padStart(2, '0') + 
         b.toString(16).padStart(2, '0');
}

// Resolve color from ExcelJS color object
function resolveExcelColor(colorObj, defaultColor = null) {
  if (!colorObj) return defaultColor;
  
  // ARGB color (most common)
  if (colorObj.argb) {
    return `#${colorObj.argb.slice(-6)}`;
  }
  
  // Theme color
  if (colorObj.theme !== undefined && colorObj.theme !== null) {
    const themeIndex = colorObj.theme;
    if (themeIndex >= 0 && themeIndex < EXCEL_THEME_COLORS.length) {
      let baseColor = EXCEL_THEME_COLORS[themeIndex];
      if (colorObj.tint) {
        baseColor = applyTint(baseColor, colorObj.tint);
      }
      return `#${baseColor}`;
    }
  }
  
  // RGB color
  if (colorObj.rgb) {
    return `#${colorObj.rgb}`;
  }
  
  // Indexed color (Excel 97-2003 palette)
  if (colorObj.indexed !== undefined) {
    // Common indexed colors
    const indexedColors = {
      0: '000000', 1: 'FFFFFF', 2: 'FF0000', 3: '00FF00',
      4: '0000FF', 5: 'FFFF00', 6: 'FF00FF', 7: '00FFFF',
      8: '000000', 9: 'FFFFFF', 10: 'FF0000', 11: '00FF00',
      12: '0000FF', 13: 'FFFF00', 14: 'FF00FF', 15: '00FFFF',
      64: '000000', 65: 'FFFFFF'
    };
    if (indexedColors[colorObj.indexed]) {
      return `#${indexedColors[colorObj.indexed]}`;
    }
  }
  
  return defaultColor;
}

// Parse Excel number format to x-data-spreadsheet format key
function parseExcelNumFmt(numFmt) {
  if (!numFmt || numFmt === 'General') return null;
  
  const fmt = numFmt.toLowerCase();
  
  // Text format
  if (numFmt === '@') return 'text';
  
  // Percentage
  if (fmt.includes('%')) return 'percent';
  
  // Currency
  if (numFmt.includes('$')) return 'usd';
  if (numFmt.includes('€')) return 'eur';
  if (numFmt.includes('¥') || numFmt.includes('￥')) return 'rmb';
  if (numFmt.includes('£')) return 'usd'; // fallback
  
  // Date/Time - check for date patterns first
  const hasDate = /[dmy]/i.test(fmt) && !/[hs]/i.test(fmt);
  const hasTime = /[hs]/i.test(fmt) && !/[dmy]/i.test(fmt);
  const hasDateTime = /[dmy]/i.test(fmt) && /[hs]/i.test(fmt);
  
  if (hasDateTime) return 'datetime';
  if (hasDate) return 'date';
  if (hasTime) return 'time';
  
  // Number format
  if (fmt.includes('#') || /\d/.test(fmt)) return 'number';
  
  return null;
}

// Convert Excel column width (characters) to pixels
function excelWidthToPixels(width) {
  if (typeof width !== 'number' || !Number.isFinite(width) || width <= 0) {
    return null;
  }
  // Excel width is in "characters" where 1 character ≈ 7 pixels
  // Adding padding for accuracy
  return Math.round(width * 7.5 + 5);
}

// Convert Excel row height (points) to pixels
function excelHeightToPixels(height) {
  if (typeof height !== 'number' || !Number.isFinite(height) || height <= 0) {
    return null;
  }
  // Excel height is in points (1 point = 1.333 pixels approximately)
  return Math.round(height * 1.333);
}

// Format cell value for display
function formatCellValue(cell) {
  const value = cell.value;
  
  if (value === null || value === undefined) {
    return '';
  }
  
  // Handle formula with result
  if (cell.formula) {
    return `=${cell.formula}`;
  }
  
  // Handle formula object
  if (typeof value === 'object' && value !== null) {
    // Shared formula
    if (value.sharedFormula) {
      return `=${value.sharedFormula}`;
    }
    
    // Formula with result
    if (value.formula) {
      return `=${value.formula}`;
    }
    
    // Rich text
    if (value.richText && Array.isArray(value.richText)) {
      return value.richText.map(rt => rt.text || '').join('');
    }
    
    // Error value
    if (value.error) {
      return value.error;
    }
    
    // Hyperlink
    if (value.hyperlink) {
      return value.text || value.hyperlink;
    }
    
    // Date object
    if (value instanceof Date) {
      // Format as readable date
      const year = value.getFullYear();
      const month = String(value.getMonth() + 1).padStart(2, '0');
      const day = String(value.getDate()).padStart(2, '0');
      const hours = value.getHours();
      const minutes = value.getMinutes();
      const seconds = value.getSeconds();
      
      if (hours === 0 && minutes === 0 && seconds === 0) {
        return `${month}/${day}/${year}`;
      }
      return `${month}/${day}/${year} ${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
    }
    
    // Result from formula
    if ('result' in value) {
      if (value.result instanceof Date) {
        const d = value.result;
        return `${d.getMonth() + 1}/${d.getDate()}/${d.getFullYear()}`;
      }
      return String(value.result ?? '');
    }
  }
  
  // Boolean
  if (typeof value === 'boolean') {
    return value ? 'TRUE' : 'FALSE';
  }
  
  // Number
  if (typeof value === 'number') {
    // Check if it's a date serial number (Excel stores dates as numbers)
    // Excel dates start from 1900-01-01 (serial number 1)
    if (cell.numFmt && /[dmy]/i.test(cell.numFmt) && value > 0 && value < 2958466) {
      // Convert Excel serial to JS date
      const excelEpoch = new Date(1899, 11, 30); // Excel epoch
      const jsDate = new Date(excelEpoch.getTime() + value * 24 * 60 * 60 * 1000);
      const month = String(jsDate.getMonth() + 1).padStart(2, '0');
      const day = String(jsDate.getDate()).padStart(2, '0');
      const year = jsDate.getFullYear();
      return `${month}/${day}/${year}`;
    }
    return String(value);
  }
  
  return String(value);
}

// Find or add style to styles array, returning the index
function findOrAddStyle(styles, newStyle) {
  // Check if an identical style already exists
  for (let i = 0; i < styles.length; i++) {
    if (JSON.stringify(styles[i]) === JSON.stringify(newStyle)) {
      return i;
    }
  }
  // Add new style
  styles.push(newStyle);
  return styles.length - 1;
}

/**
 * Convert Excel workbook to x-data-spreadsheet format with complete formatting
 * This includes formulas, styles, merges, column widths, row heights, etc.
 * Designed to match Google Sheets import behavior.
 */
export async function xlsxToXDataSpreadsheet(fileData) {
  let workbook;
  try {
    // Try to load as XLSX with ExcelJS for complete formatting support
    workbook = new ExcelJS.Workbook();
    await workbook.xlsx.load(fileData);
  } catch (error) {
    if (error.message.includes('central directory') || error.message.includes('zip')) {
      // Fallback to XLSX library for XLS files (limited formatting)
      const xlsxWorkbook = XLSX.read(fileData, {type: 'array'});
      const out = [];
      xlsxWorkbook.SheetNames.forEach((sheetName) => {
        const worksheet = xlsxWorkbook.Sheets[sheetName];
        const sheetData = {
          name: sheetName,
          rows: {},
          cols: {},
          merges: [],
          styles: [],
          freeze: 'A1',
        };
        const range = XLSX.utils.decode_range(worksheet['!ref'] || 'A1:Z100');
        const maxRow = Math.max(range.e.r + 1, 3000);
        const maxCol = Math.max(range.e.c + 1, 26);
        if (worksheet['!merges']) {
          worksheet['!merges'].forEach(merge => {
            const start = XLSX.utils.encode_cell(merge.s);
            const end = XLSX.utils.encode_cell(merge.e);
            sheetData.merges.push(`${start}:${end}`);
          });
        }
        for (let r = 0; r < maxRow; r++) {
          sheetData.rows[r] = { cells: {} };
          for (let c = 0; c < maxCol; c++) {
            const cellAddress = XLSX.utils.encode_cell({r, c});
            const cell = worksheet[cellAddress];
            if (cell && cell.v !== undefined) {
              sheetData.rows[r].cells[c] = {
                text: String(cell.v),
                style: 0,
              };
            }
          }
        }
        sheetData.rows.len = maxRow;
        sheetData.cols.len = maxCol;
        out.push(sheetData);
      });
      return out;
    } else {
      throw error;
    }
  }
  
  const out = [];
  
  workbook.eachSheet((worksheet, sheetIndex) => {
      const sheetData = {
        name: worksheet.name || `Sheet${sheetIndex}`,
        rows: {},
        cols: {},
        merges: [],
        styles: [],
        freeze: 'A1', // Default freeze position
      };
      
      // Get actual dimensions from worksheet
      let maxRow = 3000;
      let maxCol = 26;
      
      // Try to get dimensions from worksheet
      if (worksheet.dimensions) {
        const dims = worksheet.dimensions;
        if (dims.model) {
          maxRow = Math.max(maxRow, dims.model.bottom || 100);
          maxCol = Math.max(maxCol, dims.model.right || 26);
        } else {
          maxRow = Math.max(maxRow, dims.bottom || 100);
          maxCol = Math.max(maxCol, dims.right || 26);
        }
      }
      
      // Also check rowCount and columnCount
      if (worksheet.rowCount) {
        maxRow = Math.max(maxRow, worksheet.rowCount);
      }
      if (worksheet.columnCount) {
        maxCol = Math.max(maxCol, worksheet.columnCount);
      }
      
      // Parse merged cells and build lookup
      const mergedRanges = [];
      const mergedCellLookup = new Map(); // "row,col" -> { isTopLeft, topLeftRow, topLeftCol, rowSpan, colSpan }
      
      // Get merges from worksheet model
      let rawMerges = [];
      if (worksheet.model && worksheet.model.merges) {
        rawMerges = worksheet.model.merges;
      } else if (worksheet._merges) {
        rawMerges = Object.values(worksheet._merges);
      }
      
      if (Array.isArray(rawMerges)) {
        rawMerges.forEach(merge => {
          let mergeString = null;
          
          if (typeof merge === 'string') {
            mergeString = merge;
          } else if (merge && typeof merge === 'object') {
            if (merge.model) {
              mergeString = merge.model;
            } else if (typeof merge.toString === 'function') {
              const str = merge.toString();
              if (str.includes(':')) {
                mergeString = str;
              }
            }
            // Handle range object format
            if (!mergeString && merge.top !== undefined && merge.left !== undefined) {
              const topRow = merge.top;
              const leftCol = merge.left;
              const bottomRow = merge.bottom || merge.top;
              const rightCol = merge.right || merge.left;
              mergeString = `${indexToColumn(leftCol - 1)}${topRow}:${indexToColumn(rightCol - 1)}${bottomRow}`;
            }
          }
          
          if (mergeString && mergeString.includes(':')) {
            // Add to sheet merges array
            sheetData.merges.push(mergeString);
            
            // Parse the range
            const [startRef, endRef] = mergeString.split(':');
            const startMatch = startRef.match(/^([A-Z]+)(\d+)$/i);
            const endMatch = endRef.match(/^([A-Z]+)(\d+)$/i);
            
            if (startMatch && endMatch) {
              const startCol = columnToIndex(startMatch[1].toUpperCase());
              const startRow = parseInt(startMatch[2], 10) - 1;
              const endCol = columnToIndex(endMatch[1].toUpperCase());
              const endRow = parseInt(endMatch[2], 10) - 1;
              
              const rowSpan = endRow - startRow + 1;
              const colSpan = endCol - startCol + 1;
              
              mergedRanges.push({
                startRow,
                startCol,
                endRow,
                endCol,
                rowSpan,
                colSpan
              });
              
              // Build lookup for all cells in merge
              for (let r = startRow; r <= endRow; r++) {
                for (let c = startCol; c <= endCol; c++) {
                  const key = `${r},${c}`;
                  mergedCellLookup.set(key, {
                    isTopLeft: r === startRow && c === startCol,
                    topLeftRow: startRow,
                    topLeftCol: startCol,
                    rowSpan,
                    colSpan
                  });
                }
              }
              
              // Update max dimensions
              maxRow = Math.max(maxRow, endRow + 1);
              maxCol = Math.max(maxCol, endCol + 1);
            }
          }
        });
      }
      
      // Process freeze panes
      if (worksheet.views && worksheet.views.length > 0) {
        const view = worksheet.views[0];
        if (view.state === 'frozen') {
          const frozenRow = view.ySplit || 0;
          const frozenCol = view.xSplit || 0;
          if (frozenRow > 0 || frozenCol > 0) {
            sheetData.freeze = `${indexToColumn(frozenCol)}${frozenRow + 1}`;
          }
        }
      }
      
      // Process column widths and hidden columns
      worksheet.columns.forEach((col, colIndex) => {
        const colData = {};
        let hasData = false;
        
        if (col.width) {
          const pxWidth = excelWidthToPixels(col.width);
          if (pxWidth) {
            colData.width = pxWidth;
            hasData = true;
          }
        }
        
        if (col.hidden) {
          colData.hide = true;
          hasData = true;
        }
        
        if (hasData) {
          sheetData.cols[colIndex] = colData;
        }
      });
      
      // Process all rows
      for (let ri = 0; ri < maxRow; ri++) {
        const excelRow = worksheet.getRow(ri + 1); // ExcelJS uses 1-based indexing
        
        let rowData = null;
        
        // Handle row height
        if (excelRow.height) {
          const pxHeight = excelHeightToPixels(excelRow.height);
          if (pxHeight) {
            rowData = rowData || {};
            rowData.height = pxHeight;
          }
        }
        
        // Handle hidden rows
        if (excelRow.hidden) {
          rowData = rowData || {};
          rowData.hide = true;
        }
        
        const cells = {};
        let hasAnyCells = false;
        
        for (let ci = 0; ci < maxCol; ci++) {
          const mergeKey = `${ri},${ci}`;
          const mergeInfo = mergedCellLookup.get(mergeKey);
          
          // Skip cells that are part of a merge but not the top-left
          if (mergeInfo && !mergeInfo.isTopLeft) {
            continue;
          }
          
          const excelCell = excelRow.getCell(ci + 1); // ExcelJS uses 1-based indexing
          
          // Check if cell has content or styling
          const hasValue = excelCell.value !== null && excelCell.value !== undefined;
          const hasFont = excelCell.font && Object.keys(excelCell.font).length > 0;
          const hasFill = excelCell.fill && excelCell.fill.type === 'pattern' && excelCell.fill.fgColor;
          const hasBorder = excelCell.border && Object.keys(excelCell.border).length > 0;
          const hasAlignment = excelCell.alignment && Object.keys(excelCell.alignment).length > 0;
          const hasStyle = hasFont || hasFill || hasBorder || hasAlignment;
          const isMergeTopLeft = mergeInfo && mergeInfo.isTopLeft;
          
          if (!hasValue && !hasStyle && !isMergeTopLeft) {
            continue;
          }
          
          hasAnyCells = true;
          const cellData = {};
          
          // Extract cell value
          cellData.text = formatCellValue(excelCell);
          
          // Add merge info to top-left cell of merged region
          if (isMergeTopLeft && (mergeInfo.rowSpan > 1 || mergeInfo.colSpan > 1)) {
            cellData.merge = [mergeInfo.rowSpan - 1, mergeInfo.colSpan - 1];
          }
          
          // Build style object
          const style = {};
          
          // Font properties
          if (excelCell.font) {
            const font = excelCell.font;
            
            style.font = {
              name: font.name || 'Arial',
              size: font.size || 10,
              bold: !!font.bold,
              italic: !!font.italic,
            };
            
            // Font color
            const fontColor = resolveExcelColor(font.color, null);
            if (fontColor) {
              style.color = fontColor;
            }
            
            // Strikethrough
            if (font.strike) {
              style.strike = true;
            }
            
            // Underline
            if (font.underline) {
              style.underline = true;
            }
          }
          
          // Background color
          if (excelCell.fill && excelCell.fill.type === 'pattern') {
            const bgColor = resolveExcelColor(excelCell.fill.fgColor, null);
            if (bgColor && bgColor.toUpperCase() !== '#FFFFFF') {
              style.bgcolor = bgColor;
            }
          }
          
          // Alignment
          if (excelCell.alignment) {
            const align = excelCell.alignment;
            
            if (align.horizontal) {
              const h = align.horizontal.toLowerCase();
              if (h === 'center' || h === 'centerContinuous') {
                style.align = 'center';
              } else if (h === 'right') {
                style.align = 'right';
              } else if (h === 'left') {
                style.align = 'left';
              }
            }
            
            if (align.vertical) {
              const v = align.vertical.toLowerCase();
              if (v === 'middle' || v === 'center') {
                style.valign = 'middle';
              } else if (v === 'top') {
                style.valign = 'top';
              } else if (v === 'bottom') {
                style.valign = 'bottom';
              }
            }
            
            if (align.wrapText) {
              style.textwrap = true;
            }
          }
          
          // Borders
          if (excelCell.border) {
            const border = {};
            const sides = ['top', 'bottom', 'left', 'right'];
            
            sides.forEach(side => {
              const borderSide = excelCell.border[side];
              if (borderSide && borderSide.style) {
                const borderColor = resolveExcelColor(borderSide.color, '#000000');
                border[side] = [borderSide.style, borderColor];
              }
            });
            
            if (Object.keys(border).length > 0) {
              style.border = border;
            }
          }
          
          // Number format
          if (excelCell.numFmt) {
            const formatKey = parseExcelNumFmt(excelCell.numFmt);
            if (formatKey) {
              style.format = formatKey;
            }
          }
          
          // Add style if it has any properties
          if (Object.keys(style).length > 0) {
            cellData.style = findOrAddStyle(sheetData.styles, style);
          }
          
          cells[ci] = cellData;
        }
        
        // Add cells to row if there are any
        if (hasAnyCells || rowData) {
          sheetData.rows[ri] = rowData || {};
          if (hasAnyCells) {
            sheetData.rows[ri].cells = cells;
          }
        }
      }
      
      // Set lengths
      sheetData.rows.len = maxRow;
      sheetData.cols.len = maxCol;
      
      out.push(sheetData);
    });
    
    // Ensure at least one sheet
    if (out.length === 0) {
      out.push({
        name: 'Sheet1',
        rows: { len: 3000 },
        cols: { len: 26 },
        merges: [],
        styles: [],
        freeze: 'A1',
      });
    }
    
    return out;
}
